import { ComponentProps, MouseEvent, ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';
// eslint-disable-next-line import/no-namespace
import * as SS from 'styled-system';

import { Hide } from 'components/Hide';
import { Icon } from 'components/Icon';
import { Link as ReactRouterLink } from 'components/RouterLink';
import { colors, shadows } from 'theme/theme';
import { ClickOutside } from 'utils/ClickOutside';

const navLinkCss = css<{ $blue?: boolean }>`
  font-size: inherit;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  &:disabled {
    opacity: 1/4;
  }
  ${SS.fontWeight};

  color: ${(props) =>
    props.$blue ? colors.brandBlue : colors.mediumContentGrey};
`;

const NavLink = styled.a<{ $blue?: boolean }>`
  ${navLinkCss};
`;

const RouterNavLink = styled(ReactRouterLink)<{ $blue?: boolean }>`
  ${navLinkCss}
`;

const MenuList = styled.div<{
  $up?: boolean;
  $width?: number;
  $height?: string;
}>`
  max-width: 270px;
  min-width: 200px;
  overflow-x: hidden;

  ${(props) =>
    props.$up &&
    css`
      display: flex;
      flex-direction: column-reverse;
    `}
  ${(props) =>
    props.$width &&
    css`
      min-width: ${props.$width}px;
    `}
  ${(props) =>
    props.$height &&
    css`
      max-height: ${props.$height};
      overflow-y: scroll;
    `}
`;

const StyledDropdownMenuNav = styled.button<{
  $open: boolean;
  $right?: boolean;
  $blue?: boolean;
}>`
  z-index: 2100;
  display: flex;
  align-items: center;
  margin-left: 6px;
  margin-right: 6px;
  padding: 8px 12px;
  font-size: inherit;

  ${(props) =>
    props.$open &&
    css`
      border-radius: 4px;
      background-color: ${colors.calloutGrey};
    `}

  ${(props) =>
    props.$right &&
    css`
      justify-content: end;
    `};

  color: ${(props) =>
    props.$blue ? colors.brandBlue : colors.mediumContentGrey};

  &:hover,
  &:focus {
    color: ${colors.brandBlue};
  }
`;

type HideOptions = {
  xs?: boolean;
  sm?: boolean;
  md?: boolean;
  lg?: boolean;
  xl?: boolean;
};

function DropdownMenuNav({
  open,
  up,
  children,
  hideArrow,
  onClick,
  qaId,
  ariaLabel,
  $open,
  $right,
}: {
  open: boolean;
  up?: boolean;
  children: ReactNode;
  hideArrow?: HideOptions;
  ariaLabel?: string;
  qaId?: string;
} & ComponentProps<typeof StyledDropdownMenuNav>) {
  return (
    <StyledDropdownMenuNav
      onClick={onClick}
      data-qa-id={qaId}
      aria-label={ariaLabel}
      $open={$open}
      $right={$right}
    >
      <div>{children}</div>
      {/* TODO: Fix this the next time the file is edited. */}
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <Hide {...hideArrow}>
        <div style={{ marginTop: -2, marginLeft: 5 }}>
          <Icon
            name={
              (open && !up) || (!open && up) ? 'chevron-up' : 'chevron-down'
            }
            size={9}
          />
        </div>
      </Hide>
    </StyledDropdownMenuNav>
  );
}

const menuItemCss = css`
  display: block;
  margin: 0;
  padding: 10px 12px;
  cursor: pointer;
  color: ${colors.lightContentGrey};
  text-decoration: none;
  font-size: inherit;
  font-weight: normal;
  white-space: unset;

  &:hover {
    background-color: ${colors.inputGrey};
    text-decoration: none;
    color: ${colors.mediumContentGrey};
  }

  .truncate {
    overflow: hidden;
    /* stylelint-disable-next-line value-no-vendor-prefix */
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
`;

export const PageHeaderDropdownMenuItem = styled(NavLink)<{
  $disabled?: boolean;
  $logoutLink?: boolean;
}>`
  ${menuItemCss};

  ${(props) =>
    props.$logoutLink &&
    css`
      color: ${colors.decorativePoppy};

      &:hover {
        color: ${colors.decorativePoppy};
        background-color: ${colors.lightRed};
      }
    `}
`;

export const DropdownMenuItemRouter = styled(RouterNavLink)`
  ${menuItemCss};
`;

const DropdownMenuContainer = styled.div<{
  $right?: boolean;
  $open?: boolean;
}>`
  position: absolute;
  z-index: 2000;
  overflow: hidden;
  box-shadow: ${shadows.highest};
  background-color: white;
  border-radius: 4px;
  margin-top: 4px;
  transition: all 0.166s;
  ${(props) =>
    props.$open
      ? `
    display: block;
  `
      : `
    display: none;
  `}

  ${(props) =>
    props.$right
      ? css`
          right: 0;
        `
      : css`
          left: 0;
        `};
`;
const StyledDropdownMenu = styled.div`
  position: relative;
`;

type Props = {
  title: (arg0: boolean) => ReactNode;
  width?: number;
  height?: string;
  right?: boolean;
  up?: boolean;
  children:
    | ReactNode
    | ((data: { closeMenu: () => void; open: boolean }) => ReactNode);
  'data-qa-id': string;
  openQaId?: string;
  closeQaId?: string;
  dropdownQaId?: string;
  'aria-label'?: string;
  blue?: boolean;
  hideArrow?: HideOptions;
  onClick?: (open: boolean) => void;
  onClose?: (open: boolean) => void;
};

export function PageHeaderDropdownMenu({
  title,
  width,
  height,
  right,
  up,
  children,
  'data-qa-id': qaId,
  openQaId,
  closeQaId,
  dropdownQaId,
  'aria-label': ariaLabel,
  blue,
  hideArrow,
  onClick,
  onClose,
  ...props
}: Props) {
  const [open, setOpen] = useState(false);

  const toggleMenu = (e: MouseEvent) => {
    if (open && onClose) {
      onClose(!open);
    }

    setOpen(!open);
    e.preventDefault();
  };

  const closeMenu = () => {
    if (open && onClose) {
      onClose(false);
    }

    setOpen(false);
  };

  const dropdownNavClick = (e: MouseEvent) => {
    toggleMenu(e);

    if (onClick) {
      onClick(!open);
    }
  };

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/function-component-definition, react/no-unstable-nested-components
  const DropdownNav = () => (
    <DropdownMenuNav
      onClick={dropdownNavClick}
      open={open}
      up={up}
      qaId={qaId ? `${qaId}-toggle` : undefined}
      aria-label={ariaLabel}
      $blue={blue}
      $right={right}
      $open={open}
      hideArrow={hideArrow}
    >
      {title(open)}
    </DropdownMenuNav>
  );

  return (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/jsx-props-no-spreading
    <StyledDropdownMenu {...props}>
      <DropdownNav />
      <DropdownMenuContainer $right={right} $open={open} data-qa-id={qaId}>
        <ClickOutside onClickOutside={closeMenu}>
          <MenuList $up={up} $width={width} $height={height}>
            {typeof children === 'function'
              ? children({ closeMenu, open })
              : children}
          </MenuList>
        </ClickOutside>
      </DropdownMenuContainer>
    </StyledDropdownMenu>
  );
}

export const PageHeaderDropdownMenuSection = styled.div<{
  $noBorder?: boolean;
}>`
  ${(p) =>
    p.$noBorder ? null : `border-top: solid 1px ${colors.selectionGrey};`}
  width: 100%;
`;

export const PageHeaderDropdownMenuSectionTitle = styled.div`
  color: ${colors.lightContentGrey};
  font-size: 12px;
  font-weight: 400;
  text-transform: uppercase;
  text-decoration: none;
  letter-spacing: 1.8px;
  white-space: nowrap;
  display: block;
  margin: 0;
  padding: 12px;
  background-color: ${colors.backgroundBlue};
`;
